// src/pages/Billing/SubscriptionLog.js  (or wherever you place it)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import backendURL from '../../config'; // Your backend URL config
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper, // Wrap TableContainer in Paper for elevation/border
    Chip,
    Tooltip,
} from '@mui/material';

// --- Helper Functions ---
const IsLoadingSpinner = ({ size = 'medium' }) => {
    const muiSize = size === 'small' ? 20 : size === 'large' ? 40 : 30;
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}><CircularProgress size={muiSize} /></Box>;
};
const ErrorMessage = ({ message }) => {
    if (!message) return null;
    return <Alert severity="error" sx={{ my: 2 }}>{typeof message === 'object' ? JSON.stringify(message) : String(message)}</Alert>;
};

// Helper to format currency (assuming amounts are in major units like dollars/euros)
const formatCurrency = (amount, currencyCode = 'usd') => {
    if (typeof amount !== 'number') return 'N/A';
    try {
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD', // Default to USD if not provided
             minimumFractionDigits: 2,
             maximumFractionDigits: 2,
         }).format(amount); // Amount is already in major units
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         return `$${amount.toFixed(2)}`; // Basic fallback
    }
 };

// Helper to format date/time nicely
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString(undefined, { // Use locale default
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit' // Removed hour12 for potentially less ambiguity
        });
    } catch (e) {
        console.warn("Date formatting failed:", e);
        return dateString; // Fallback
    }
};

// Helper to make change types more readable
const formatChangeType = (type) => {
    if (!type) return 'Unknown';
    // Convert snake_case to Title Case
    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

// --- SubscriptionLog Component ---
const SubscriptionLog = ({ onError }) => { // Added onError prop
    const [history, setHistory] = useState([]);
    const [IsLoading, setIsIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const onErrorRef = useRef(onError); // Use ref for stable callback
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

        // --- Configuration ---
    const API_BASE_URL = backendURL; // Ensure this is correctly set in your config.js

    // --- Fetch History Data ---
    const fetchHistory = useCallback(async () => {
        console.log("Fetching subscription history...");
        setIsIsLoading(true);
        setError(null);
        setHistory([]); // Clear previous history


        try {
            const response = await axiosInstance.get(`/subscriptions/history`);

            console.log("Subscription History Response:", response.data);

            // Check if response.data is the array or nested under response.data.data
            let historyData = null;
            if (Array.isArray(response.data)) {
                 historyData = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                 historyData = response.data.data;
            }

            if (historyData) {
                setHistory(historyData);
            } else {
                 console.warn("Subscription history API did not return a valid array:", response.data);
                 setError("Received unexpected data format for subscription history.");
            }

        } catch (err) {
            console.error("Error fetching subscription history:", err);
             const errorMsg = err.response?.data?.message || err.message || "Could not load subscription history.";
            setError(errorMsg);
            if (err.response?.status === 401 && onErrorRef.current) {
                 onErrorRef.current("Session expired. Please log in again."); // Specific message for 401
            } else if (onErrorRef.current) {
                 onErrorRef.current(errorMsg); // Notify parent of other errors
            }
        } finally {
            setIsIsLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]); // Fetch on mount and when fetchHistory changes

    // --- Render Logic ---
    const renderContent = () => {
        if (IsLoading) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><IsLoadingSpinner /></Box>;
        }

        if (error) {
            // Error already displayed by parent via callback, but keep local one as fallback
             return <ErrorMessage message={error} />;
        }

        if (history.length === 0) {
            return <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No subscription changes found.</Typography>;
        }

        return (
            <TableContainer component={Paper} variant="outlined"> {/* Use outlined Paper */}
                <Table sx={{ minWidth: 650 }} aria-label="subscription log table">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Change Type</TableCell>
                            <TableCell>From Plan</TableCell>
                            <TableCell>To Plan</TableCell>
                            <TableCell align="right">Amount / Credit</TableCell>
                            <TableCell>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.map((item) => (
                            <TableRow
                                key={item.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                hover // Add hover effect
                            >
                                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatDateTime(item.change_timestamp)}
                                </TableCell>
                                <TableCell>
                                    {/* Use Chip for better visual separation */}
                                    <Chip label={formatChangeType(item.change_type)} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{item.previous_plan?.display_name || 'N/A'}</TableCell>
                                <TableCell>{item.new_plan?.display_name || 'N/A'}</TableCell>
                                <TableCell align="right">
                                    {/* Display charge or credit distinctly */}
                                    {item.amount_charged != null && item.amount_charged > 0 ? (
                                        <Tooltip title="Amount Charged">
                                            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                                {formatCurrency(item.amount_charged)}
                                            </Typography>
                                        </Tooltip>
                                    ) : item.amount_credited != null && item.amount_credited > 0 ? ( // Check > 0 for credits too
                                         <Tooltip title="Credit Applied/Issued">
                                             <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                                 ({formatCurrency(item.amount_credited)}) {/* Show credits in parentheses */}
                                             </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography> // No amount change
                                    )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 250 }}> {/* Allow notes to wrap slightly if needed */}
                                   <Tooltip title={item.notes || ''} arrow>
                                        {/* Display notes or placeholder */}
                                        <Typography variant="body2" color="text.secondary" noWrap={!item.notes?.includes('\n')}> {/* Allow wrap if notes have newlines */}
                                           {item.notes || '-'}
                                        </Typography>
                                   </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Main component structure (no Card wrapper needed if used in TabPanel)
    return (
         <Box>
             {/* Title is likely handled by the Tab label in BillingPage */}
             {/* <Typography variant="h6" component="div" gutterBottom sx={{ mb: 2 }}>
                 Subscription Log
             </Typography> */}
             {renderContent()}
         </Box>
    );
};

export default SubscriptionLog;